import { type ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { type AdminUser } from '@/types/user';
import { UserActions } from '../UserActions/UserActions';

export const roleLabels = {
  super_admin: 'Super Admin',
  organization_manager: 'Organization Manager',
  venue_manager: 'Venue Manager',
  venue_operator: 'Venue Operator',
  viewer: 'Viewer',
};

interface GetColumnsOptions {
  onEdit?: (user: AdminUser) => void;
  onDelete?: (user: AdminUser) => void;
  onToggleStatus?: (user: AdminUser) => Promise<void>;
}

export const getColumns = ({
  onEdit,
  onDelete,
  onToggleStatus,
}: GetColumnsOptions = {}): ColumnDef<AdminUser>[] => [
  {
    accessorKey: 'full_name',
    header: 'Name',
    filterFn: 'includesString',
    enableSorting: true,
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.full_name || 'No Name'}</div>
        <div className="text-sm text-muted-foreground">
          {row.original.phone_number || row.original.email || 'No contact info'}
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'role',
    header: 'Role',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const roleA = Array.isArray(rowA.original.role) ? rowA.original.role[0] : rowA.original.role;
      const roleB = Array.isArray(rowB.original.role) ? rowB.original.role[0] : rowB.original.role;
      return roleA.localeCompare(roleB);
    },
    cell: ({ row }) => {
      const roles = Array.isArray(row.original.role)
        ? row.original.role
        : [row.original.role];
      
      return (
        <div className="flex flex-wrap gap-1">
          {roles.map((role, index) => (
            <Badge key={index} variant="secondary" className="capitalize">
              {roleLabels[role as keyof typeof roleLabels] || role}
            </Badge>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'organization.name',
    header: 'Organization',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const orgA = rowA.original.organization?.name || 'Urban Finch';
      const orgB = rowB.original.organization?.name || 'Urban Finch';
      return orgA.localeCompare(orgB);
    },
    cell: ({ row }) =>
      row.original.organization?.name ? (
        row.original.organization.name
      ) : (
        <Badge variant="default" className="bg-green-100 text-green-800">
          Urban Finch
        </Badge>
      ),
  },
  {
    accessorKey: 'is_active',
    header: 'Status',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      return rowA.original.is_active === rowB.original.is_active ? 0 : rowA.original.is_active ? -1 : 1;
    },
    cell: ({ row }) => (
      <Badge
        variant={row.original.is_active ? 'default' : 'secondary'}
        className={row.original.is_active ? 'bg-green-100 text-green-800' : ''}
      >
        {row.original.is_active ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      return new Date(rowA.original.created_at).getTime() - new Date(rowB.original.created_at).getTime();
    },
    cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
  },
  {
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <UserActions
        user={row.original}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleStatus={onToggleStatus}
      />
    ),
    size: 50,
    meta: {
      align: 'right',
    },
  },
];
